import { Component, OnInit, Inject, OnDestroy } from '@angular/core';
import { NgxFileDropEntry, FileSystemFileEntry } from 'ngx-file-drop';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { PersonType } from 'src/app/services/models/enums';
import {
  BulkLoadFileService,
  CreateBulkLoadFileXlsxResponseDto,
  FollowUpTypeDto,
} from 'src/generated-sources/openapi/bulk-load-service/bulk-load-file';
import { WalletDataService } from 'src/app/services/data/wallet-data.service';
import { WalletResponseDto } from 'src/generated-sources/openapi/wallet-service/wallet';
import { Subject, take, takeUntil } from 'rxjs';
import { toBase64 } from 'src/app/helpers/file-utils';
import { SnackbarService } from 'src/app/services/snackbar.service';
import { FileUtilsService } from 'src/app/services/file-utils.service';
import { ConfirmationDialogProps } from 'src/app/shared/components/modals/confirmation-dialog/confirmation-dialog.component';
import { followUpTimeOptions } from '../../constants';

const MAX_FILE_SIZE_MB = 2;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

@Component({
  selector: 'app-file-upload-modal',
  templateUrl: './file-upload-modal.component.html',
  styleUrls: ['./file-upload-modal.component.scss'],
  standalone: false,
})
export class FileUploadModalComponent implements OnInit, OnDestroy {
  STATES = {
    IDLE: Symbol.for('FILE_IDLE'),
    FILE_LOAD: Symbol.for('FILE_LOAD'),
    FILE_COMPLETE: Symbol.for('FILE_COMPLETE'),
  };

  private readonly destroy$ = new Subject<void>();
  STATE = this.STATES.IDLE;
  initFront: boolean = false;
  isBusy: boolean = true;
  country = localStorage.getItem('userCountry');
  countryEnabled: boolean = false;
  isRelational: boolean = false;
  personType: PersonType;
  public link;
  public linkTemplate;
  processed: boolean;

  followUpTimes = followUpTimeOptions;

  selectedFollowUpTime: FollowUpTypeDto;
  selectedWallet: WalletResponseDto;
  public files: File[] = [];
  public rawFiles;
  acceptedFormats = ['.xls', '.xlsx'];
  constructor(
    private readonly dialogRef: MatDialogRef<FileUploadModalComponent>,
    private readonly bulkLoadFileService: BulkLoadFileService,
    private readonly walletDataService: WalletDataService,
    private readonly snackbarService: SnackbarService,
    private readonly fileUtilsService: FileUtilsService,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.linkTemplate && typeof this.linkTemplate.unsubscribe === 'function') {
      this.linkTemplate.unsubscribe();
    }
  }

  ngOnInit() {
    this.validateCountry();
    this.validateBusy();
    this.selectedFollowUpTime = 0;
  }

  validateCountry() {
    this.countryEnabled = this.country !== 'CL';
  }

  setActionCl(personType: string) {
    this.countryEnabled = true;
    if (personType) {
      this.personType = personType as PersonType;
      this.isRelational = true;
    }
  }

  async commitFiles() {
    this.STATE = this.STATES.FILE_LOAD;
    await Promise.all(
      this.files.map(async (file) => {
        const fileBase64 = await toBase64(file);

        const createBulkLoadFileXlsxRequestDto = {
          walletId: this.selectedWallet.id,
          walletName: this.selectedWallet.name,
          followUpType: this.selectedFollowUpTime,
          file: fileBase64,
          fileName: file.name,
        };

        this.bulkLoadFileService.create({ createBulkLoadFileXlsxRequestDto }).subscribe({
          next: (response: CreateBulkLoadFileXlsxResponseDto) => {
            this.STATE = this.STATES.FILE_COMPLETE;
            this.rawFiles = null;
            this.files = [];
            this.snackbarService.normal(
              $localize`:@@message_file_load_complete:Terminamos de procesar correctamente la carga masiva.`,
            );
          },
          error: ({ error }) => {
            const errorMessage = error?.errors?.[0]?.message || '';
            this.STATE = this.STATES.IDLE;
            this.snackbarService.error(errorMessage, {
              duration: 10000,
            });
          },
        });
      }),
    );
  }

  public cancel() {
    this.rawFiles = null;
    this.files = [];
  }

  public dropped(files: NgxFileDropEntry[]) {
    const allowedFiles = this.getFilesWithAllowedType(files);

    if (allowedFiles.length < files.length) {
      this.snackbarService.normal(
        $localize`:@@js_WarnInvalidMassiveFileType:Solo se admiten archivos con las siguientes extensiones: ` +
          this.acceptedFormats.join(', '),
      );
    }

    if (!allowedFiles.length) return;

    for (const droppedFile of files) {
      this.rawFiles = files;
      if (droppedFile.fileEntry.isFile) {
        const fileEntry = droppedFile.fileEntry as FileSystemFileEntry;
        fileEntry.file((file: File) => {
          if (file.size > MAX_FILE_SIZE_BYTES) {
            this.snackbarService.normal(
              $localize`:@@FileUploadTooLarge: El tamaño del archivo excede de los permitidos 2 MB.`,
            );
            return;
          }
          this.files.push(file);
        });
      }
    }
  }

  private getFilesWithAllowedType(files: NgxFileDropEntry[]) {
    return files.filter((droppedFile) => {
      const { name: fileName } = droppedFile.fileEntry;
      const fileExtension = fileName.split('.').pop()?.toLowerCase() || '';
      return this.acceptedFormats.includes(`.${fileExtension}`);
    });
  }

  closeModal(result = null) {
    this.processed = false;
    this.dialogRef.close(result);
  }

  validateBusy() {
    this.bulkLoadFileService
      .containsBusyStatus()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ busy }) => {
          this.isBusy = Boolean(busy);
          this.openFrontInit();
        },
      });
  }

  openFrontInit() {
    if (this.isBusy) {
      this.processed = false;
    }
    this.initFront = true;
  }

  handleDownloadTemplate() {
    this.linkTemplate = this.bulkLoadFileService
      .downloadBulkLoadTemplateFileXlsx('response')
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.fileUtilsService.downloadBlobFile(response, 'template-inclusao-massiva-documentos');
          this.snackbarService.normal(
            $localize`:@@js_SuccessFileDownload:La descarga del archivo se completó correctamente`,
          );
        },
        error: (error) => {
          this.snackbarService.error(
            $localize`:@@js_ErrorFileDownload:Error al intentar descargar el archivo`,
          );
        },
      });
  }

  handleDownloadLayout() {
    this.linkTemplate = this.bulkLoadFileService
      .downloadBulkLoadLayoutFilePdf('response')
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.fileUtilsService.downloadBlobFile(
            response,
            'layout-inclusao-massiva-documentos',
            '.pdf',
          );
          this.snackbarService.normal(
            $localize`:@@js_SuccessFileDownload:La descarga del archivo se completó correctamente`,
          );
        },
        error: (error) => {
          this.snackbarService.error(
            $localize`:@@js_ErrorFileDownload:Error al intentar descargar el archivo`,
          );
        },
      });
  }

  isState(DESIRED_STATE) {
    return this.STATE === DESIRED_STATE;
  }

  openFileSelectorPoxy(cb: Function = () => {}) {
    cb();
  }

  scrollTo(className: string): void {
    setTimeout(() => {
      const elementList = document.querySelectorAll('.' + className);
      const element = elementList[0] as HTMLElement;
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 500);
  }

  get confirmationDialogProps(): ConfirmationDialogProps {
    return {
      title: $localize`:@@performMassiveLoad:Realizar carga masiva`,
      cancelButton: {
        hide: true,
      },
    };
  }
}
